import { Dispatch, useCallback } from 'react';
import { WidgetConfigs } from 'aerosync-web-sdk';
import { ExecutionResult } from 'graphql';
import { useMutation } from '@apollo/client';

import { ERNIE_DEFAULT_MESSAGES } from 'shared/constants';
import { StartPreAuthCheckDocument, StartPreAuthCheckResponseStatus } from 'src/gql/graphql';
import type { StartPreAuthCheckMutation } from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';
import { useDutchiePayAnalytics } from 'src/payments/hooks';
import { AeroSyncPreAuthEvent } from 'src/payments/constants';
import { formatPaymentsError } from 'src/payments/hooks/use-payments-error/use-payments-error';
import { PusherPreAuthEvent, PusherStatus } from '../types';
import { usePreAuthPromise } from './use-pre-auth-promise';
import { usePreAuthTimeout } from './use-pre-auth-timeout';
import { usePreAuthPusherSubscription } from './use-pre-auth-pusher';
import { PreAuthAction } from './use-pre-auth-reducer';

const logger = getLogger('aerosync-pre-auth');

export const FLOW_IN_PROGRESS = 'Pre-auth flow already in progress';
export const NO_DATA = 'No data returned from startPreAuthCheck';
export const PRE_AUTH_TIMEOUT = 'Pre-auth timeout';
export const PRE_AUTH_TIMEOUT_ERROR_MESSAGE = 'Pre-auth timeout occurred while waiting for a pusher response';
export const MFA_REQUIRED = 'An MFA is required to complete your order with Pay By Bank';
export const PRE_AUTH_TIMEOUT_MS = 10000;
const { PAY_BY_BANK: GENERIC_USER_ERROR } = ERNIE_DEFAULT_MESSAGES;

type OnPreAuthErrorParams = {
  type: string;
  internalMessage: string;
  userFacingMessage?: string;
  failForward: boolean;
};

export type UsePreAuthProviderEventsProps = {
  channelName: string | null;
  dispatch: Dispatch<PreAuthAction>;
};

export type UsePreAuthProviderEventsReturn = {
  cleanup: () => void;
  triggerPreAuthFlow: (paymentMethodId: string) => Promise<void>;
  handleMfaSuccess: WidgetConfigs['onSuccess'];
  handleMfaClose: WidgetConfigs['onClose'];
  handleMfaError: WidgetConfigs['onError'];
};

export const usePreAuthProviderEvents = ({
  channelName,
  dispatch,
}: UsePreAuthProviderEventsProps): UsePreAuthProviderEventsReturn => {
  const [startPreAuthCheck] = useMutation(StartPreAuthCheckDocument);
  const { trackPreAuthEvent } = useDutchiePayAnalytics();

  const {
    setPreAuthPromise,
    resolvePreAuthPromise,
    rejectPreAuthPromise,
    hasActivePreAuthPromise,
  } = usePreAuthPromise();

  const resolveAndReset = useCallback(() => {
    resolvePreAuthPromise();
    dispatch({ type: 'RESET' });
    trackPreAuthEvent(AeroSyncPreAuthEvent.FLOW_COMPLETED);
  }, [resolvePreAuthPromise, dispatch, trackPreAuthEvent]);

  const onPreAuthTimeout = useCallback(() => {
    trackPreAuthEvent(AeroSyncPreAuthEvent.FLOW_FAILED_FORWARD, {
      error: { type: PRE_AUTH_TIMEOUT, message: PRE_AUTH_TIMEOUT_ERROR_MESSAGE },
    });
    resolveAndReset();
  }, [trackPreAuthEvent, resolveAndReset]);

  const { startPreAuthTimeout, clearPreAuthTimeout } = usePreAuthTimeout(onPreAuthTimeout, PRE_AUTH_TIMEOUT_MS);

  const onPreAuthSuccess = useCallback(() => {
    clearPreAuthTimeout();
    resolveAndReset();
  }, [clearPreAuthTimeout, resolveAndReset]);

  const onPreAuthError = useCallback(
    ({ type, internalMessage, userFacingMessage = GENERIC_USER_ERROR, failForward }: OnPreAuthErrorParams) => {
      logger.error('onPreAuthError', { type, message: internalMessage, failForward });
      if (failForward) {
        trackPreAuthEvent(AeroSyncPreAuthEvent.FLOW_FAILED_FORWARD, { error: { type, message: internalMessage } });
        onPreAuthSuccess();
        return;
      }
      clearPreAuthTimeout();
      rejectPreAuthPromise(new Error(userFacingMessage));
      dispatch({ type: 'RESET' });
      trackPreAuthEvent(AeroSyncPreAuthEvent.FLOW_FAILED, { error: { type, message: internalMessage } });
    },
    [clearPreAuthTimeout, dispatch, onPreAuthSuccess, rejectPreAuthPromise, trackPreAuthEvent]
  );

  const handlePusherResponse = useCallback(
    ({ status, meta: mfaProps }: PusherPreAuthEvent) => {
      clearPreAuthTimeout();

      const receivedValidMfaProps = Object.values(mfaProps).every(Boolean);

      if (status === PusherStatus.statusReady) {
        onPreAuthSuccess();
      } else if (status === PusherStatus.mfaRequired && receivedValidMfaProps) {
        trackPreAuthEvent(AeroSyncPreAuthEvent.MFA_PRESENTED);
        dispatch({ type: 'SHOW_MFA', mfaProps });
      } else {
        // if the status is anything other than MFA_REQUIRED or STATUS_READY, we should fail forward the pre-auth flow
        onPreAuthError({
          type: 'INVALID_PRE_AUTH_PUSHER_DATA',
          internalMessage: `Invalid Pre-Auth Pusher Data: Status: ${status} JobId: ${mfaProps.jobId} AeroPassUserUuid: ${mfaProps.aeroPassUserUuid}`,
          failForward: true,
        });
      }
    },
    [clearPreAuthTimeout, dispatch, onPreAuthSuccess, onPreAuthError, trackPreAuthEvent]
  );

  const handlePreAuthCheckResponse = useCallback(
    ({ data, errors }: ExecutionResult<StartPreAuthCheckMutation>): void => {
      if (errors?.length) {
        throw new Error(errors[0]?.message);
      }
      if (!data?.startPreAuthCheck) {
        throw new Error(NO_DATA);
      }

      const { status, channelName: responseChannelName } = data.startPreAuthCheck;

      if (status === StartPreAuthCheckResponseStatus.statusReady) {
        onPreAuthSuccess();
      } else if (status === StartPreAuthCheckResponseStatus.statusPending && responseChannelName) {
        dispatch({ type: 'START_WAITING', channelName: responseChannelName });
      } else {
        throw new Error(`Unknown pre-auth status: ${String(status)}`);
      }
    },
    [onPreAuthSuccess, dispatch]
  );

  const triggerPreAuthFlow = useCallback(
    (paymentMethodId: string): Promise<void> => {
      // Edge case to prevent multiple pre-auth flows from being triggered
      if (hasActivePreAuthPromise()) {
        return Promise.reject(new Error(FLOW_IN_PROGRESS));
      }
      return new Promise((resolve, reject) => {
        trackPreAuthEvent(AeroSyncPreAuthEvent.FLOW_STARTED);
        setPreAuthPromise(resolve, reject);

        startPreAuthCheck({ variables: { paymentMethodId } })
          .then(handlePreAuthCheckResponse)
          .catch((err) => {
            const internalMessage = `Aerosync start Pre-Auth Check Failed: ${
              err instanceof Error ? err.message : String(err)
            }`;
            const { errorMessage: userFacingMessage } = formatPaymentsError(err, undefined);
            onPreAuthError({
              type: 'START_PRE_AUTH_CHECK_FAILED',
              internalMessage,
              userFacingMessage,
              failForward: false,
            });
          });
      });
    },
    [
      hasActivePreAuthPromise,
      trackPreAuthEvent,
      setPreAuthPromise,
      startPreAuthCheck,
      handlePreAuthCheckResponse,
      onPreAuthError,
    ]
  );

  const handleMfaSuccess = useCallback(() => {
    trackPreAuthEvent(AeroSyncPreAuthEvent.MFA_COMPLETED);
    startPreAuthTimeout();
    dispatch({ type: 'MFA_SUCCESS' });
  }, [startPreAuthTimeout, dispatch, trackPreAuthEvent]);

  const handleMfaClose = useCallback(() => {
    trackPreAuthEvent(AeroSyncPreAuthEvent.MFA_ABANDONED);
    onPreAuthError({
      type: 'USER_CANCELLED',
      internalMessage: MFA_REQUIRED,
      userFacingMessage: MFA_REQUIRED,
      failForward: false,
    });
  }, [onPreAuthError, trackPreAuthEvent]);

  const handleMfaError = useCallback(
    (errorMessage: string) => {
      onPreAuthError({
        type: 'MFA_ERROR',
        internalMessage: errorMessage,
        failForward: false,
      });
    },
    [onPreAuthError]
  );

  usePreAuthPusherSubscription({
    channelName,
    onSubscribe: startPreAuthTimeout,
    onUnsubscribe: clearPreAuthTimeout,
    onEvent: handlePusherResponse,
  });

  const cleanup = useCallback(() => {
    resolvePreAuthPromise();
    clearPreAuthTimeout();
  }, [clearPreAuthTimeout, resolvePreAuthPromise]);

  return {
    cleanup,
    triggerPreAuthFlow,
    handleMfaSuccess,
    handleMfaClose,
    handleMfaError,
  };
};
